/**
 * Smooth-scroll to an in-page anchor, then CORRECT for layout shifts.
 *
 * Why this exists: the property detail page lazy-loads a dozen images (and
 * other async content) between the top CTAs and the #contact form. A native
 * smooth scroll animates to the target's position measured at click time —
 * but on slower devices (Android Chrome especially, with its smaller
 * lazy-load margins) those images decode and expand the page DURING the
 * animation, so the form has moved further down by the time the scroll
 * lands. The user ends up one section short. Desktop and iOS mostly mask
 * this by preloading more aggressively.
 *
 * Strategy: start a normal smooth scroll, then watch until scrolling
 * settles; if the target has drifted, snap-correct instantly and keep
 * watching (each correction can trigger more lazy loads). Bail out the
 * moment the user interacts, after 5 corrections, or after ~5 seconds.
 */

/** Sticky page header (h-16 md:h-20) + the sticky anchor nav (~48px). */
const headerOffset = (): number => (window.innerWidth >= 768 ? 80 : 64) + 48;

export function scrollToAnchor(anchor: string): boolean {
  const el = document.getElementById(anchor);
  if (!el) return false;

  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const targetTop = (): number =>
    Math.max(0, el.getBoundingClientRect().top + window.scrollY - headerOffset());

  window.scrollTo({ top: targetTop(), behavior: prefersReduced ? 'auto' : 'smooth' });

  let lastY = -1;
  let stableTicks = 0;
  let corrections = 0;
  let totalTicks = 0;

  const stop = () => {
    window.clearInterval(interval);
    window.removeEventListener('wheel', stop);
    window.removeEventListener('touchstart', stop);
    window.removeEventListener('keydown', stop);
  };

  const interval = window.setInterval(() => {
    totalTicks += 1;
    if (totalTicks > 40) {
      stop(); // ~5s safety cap
      return;
    }
    const y = window.scrollY;
    stableTicks = Math.abs(y - lastY) < 2 ? stableTicks + 1 : 0;
    lastY = y;

    if (stableTicks >= 2) {
      const drift = targetTop() - y;
      if (Math.abs(drift) > 8 && corrections < 5) {
        corrections += 1;
        stableTicks = 0;
        // Instant snap for corrections — a second smooth glide reads as lag.
        window.scrollTo({ top: targetTop(), behavior: 'auto' });
      } else {
        stop();
      }
    }
  }, 120);

  // The user taking over scrolling must always win over our corrections.
  // (Programmatic scrolling fires none of these events.)
  window.addEventListener('wheel', stop, { passive: true, once: true });
  window.addEventListener('touchstart', stop, { passive: true, once: true });
  window.addEventListener('keydown', stop, { once: true });

  return true;
}
